// PLA-3423: regras de bloqueio por reserva aprovada no formulário público.
// Espelha o DayWindowOverlapExistsSql do new-zing-backend (ExistsConflictAsync): cada dia de um período
// vira uma janela de horário — 1º dia início→24h, dias do meio inteiros, último dia 0h→fim (ou
// início→fim quando é 1 dia só). Sobreposição estrita: 10:00–11:00 e 11:00–12:00 não conflitam.

export interface ApprovedReservationSlot {
  id: string
  meetingRoomId: string
  meetingRoomName: string
  startDate: string // yyyy-MM-dd
  endDate: string // yyyy-MM-dd
  startTime: string // HH:mm ('' = sem horário)
  endTime: string // HH:mm ('' = sem horário)
}

export interface ReservationPeriod {
  startDate: string
  endDate: string
  startTime: string
  endTime: string
}

type MinuteWindow = [number, number]

const DAY_END_MINUTES = 24 * 60
export const TIME_SLOT_MINUTES = 30

export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return (Number.isFinite(hours) ? hours : 0) * 60 + (Number.isFinite(minutes) ? minutes : 0)
}

const minutesToTime = (total: number): string =>
  `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`

// Janela de um período (reserva existente ou candidata) num dia específico; null se o dia está fora.
const windowOnDay = (period: ReservationPeriod, day: string): MinuteWindow | null => {
  if (day < period.startDate || day > period.endDate) return null
  const isSingleDay = period.startDate === period.endDate
  const start = isSingleDay || day === period.startDate ? timeToMinutes(period.startTime || '00:00') : 0
  const end = isSingleDay || day === period.endDate
    ? (period.endTime ? timeToMinutes(period.endTime) : DAY_END_MINUTES)
    : DAY_END_MINUTES
  return [start, end]
}

const overlaps = (a: MinuteWindow, b: MinuteWindow): boolean => a[0] < b[1] && a[1] > b[0]

const slotAsPeriod = (slot: ApprovedReservationSlot): ReservationPeriod => ({
  startDate: slot.startDate,
  endDate: slot.endDate,
  startTime: slot.startTime,
  endTime: slot.endTime,
})

const addDay = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number)
  const next = new Date(year, month - 1, date + 1)
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`
}

const periodConflictsWithSlot = (period: ReservationPeriod, slot: ApprovedReservationSlot): boolean => {
  const existing = slotAsPeriod(slot)
  const from = period.startDate > existing.startDate ? period.startDate : existing.startDate
  const to = period.endDate < existing.endDate ? period.endDate : existing.endDate
  for (let day = from; day <= to; day = addDay(day)) {
    const candidateWindow = windowOnDay(period, day)
    const existingWindow = windowOnDay(existing, day)
    if (candidateWindow && existingWindow && overlaps(candidateWindow, existingWindow)) return true
  }
  return false
}

// Salas (id + nome) que têm reserva aprovada conflitando com o período.
export const findBlockedRooms = (
  slots: ApprovedReservationSlot[],
  period: ReservationPeriod
): Array<{ id: string; name: string }> => {
  if (!period.startDate || !period.endDate || !period.startTime || !period.endTime) return []
  const blocked = new Map<string, string>()
  slots.forEach(slot => {
    if (!blocked.has(slot.meetingRoomId) && periodConflictsWithSlot(period, slot)) {
      blocked.set(slot.meetingRoomId, slot.meetingRoomName)
    }
  })
  return Array.from(blocked, ([id, name]) => ({ id, name }))
}

// Horário de início bloqueado: o primeiro slot (30 min) a partir dele já está ocupado.
export const isStartTimeBlocked = (slots: ApprovedReservationSlot[], day: string, time: string): boolean => {
  if (!day) return false
  const start = timeToMinutes(time)
  const candidate: MinuteWindow = [start, Math.min(start + TIME_SLOT_MINUTES, DAY_END_MINUTES)]
  return slots.some(slot => {
    const existingWindow = windowOnDay(slotAsPeriod(slot), day)
    return existingWindow != null && overlaps(candidate, existingWindow)
  })
}

// Horário de término bloqueado: com início já escolhido, o período inteiro conflita; sem início, o
// slot (30 min) que termina nele está ocupado.
export const isEndTimeBlocked = (
  slots: ApprovedReservationSlot[],
  period: Omit<ReservationPeriod, 'endTime'>,
  time: string
): boolean => {
  if (!period.endDate) return false
  if (period.startDate && period.startTime) {
    return findBlockedRooms(slots, { ...period, endTime: time }).length > 0
  }
  const end = timeToMinutes(time)
  return isStartTimeBlocked(slots, period.endDate, minutesToTime(Math.max(end - TIME_SLOT_MINUTES, 0)))
}

// Dia sem nenhum horário de início livre dentro do funcionamento.
export const isDayFullyBlocked = (
  slots: ApprovedReservationSlot[],
  day: string,
  openTime?: string,
  closeTime?: string
): boolean => {
  const daySlots = slots.filter(slot => day >= slot.startDate && day <= slot.endDate)
  if (daySlots.length === 0) return false
  const open = timeToMinutes(openTime || '00:00')
  const close = closeTime ? timeToMinutes(closeTime) : DAY_END_MINUTES
  for (let minute = open; minute < close; minute += TIME_SLOT_MINUTES) {
    if (!isStartTimeBlocked(daySlots, day, minutesToTime(minute))) return false
  }
  return true
}
